import { parsePassthroughAttributes, parseSolimp, parseSolref } from "./attributeUtils";
import { getDirectChildElementsByTag, getFirstDirectChildByTag } from "./xmlUtils";
import type { Vector2, Vector5 } from "../../types/scene";

export interface ActuatorDefaultsState {
  actuatorClassDefaults: Record<string, Record<string, unknown>>;
  actuatorClassParentMap: Record<string, string>;
  actuatorTypeDefaults: Record<string, Record<string, unknown>>;
}

export interface TendonDefaultsState {
  tendonClassDefaults: Record<string, Record<string, string>>;
  tendonDefaultScope: Record<string, string>;
}

export interface JointDefaultsState {
  jointClassDefaults: Record<string, Record<string, unknown>>;
  jointDefaultScope: Record<string, unknown>;
}

export interface MaterialDefaultsState {
  materialClassDefaults: Record<string, Record<string, unknown>>;
  materialDefaultScope: Record<string, unknown>;
}

export interface EqualityDefaultsState {
  equalityClassDefaults: Record<string, { solref?: Vector2; solimp?: Vector5 }>;
  equalityDefaultScope: { solref?: Vector2; solimp?: Vector5 };
}

export interface CollisionDefaultsState
  extends
    ActuatorDefaultsState,
    TendonDefaultsState,
    JointDefaultsState,
    MaterialDefaultsState,
    EqualityDefaultsState {
  classScaleMap: Record<string, number[]>;
  collisionTypeMap: Record<string, string>;
  classParentMap: Record<string, string>;
  geomClassDefaults: Record<
    string,
    {
      material?: string;
      pos?: number[];
      quat?: number[];
      size?: number[];
      fromto?: number[];
      type?: string;
      mesh?: string;
      hfield?: string;
      mass?: number;
      density?: number;
      friction?: number[];
      solref?: number[];
      solimp?: Vector5;
      contype?: number;
      conaffinity?: number;
      condim?: number;
      group?: number;
    }
  >;
  cameraClassDefaults: Record<
    string,
    {
      pos?: number[];
      quat?: number[];
      euler?: number[];
      axisangle?: number[];
      xyaxes?: number[];
      zaxis?: number[];
    }
  >;
  lightClassDefaults: Record<string, Record<string, string>>;
}

export const parseDefaults = (state: CollisionDefaultsState, xmlDoc: Document): void => {
  const defaultsRoot = xmlDoc.querySelector("default");
  state.jointClassDefaults = {};
  state.jointDefaultScope = {};
  state.materialClassDefaults = {};
  state.materialDefaultScope = {};
  state.equalityClassDefaults = {};
  state.equalityDefaultScope = {};
  state.tendonClassDefaults = {};
  state.tendonDefaultScope = {};
  state.lightClassDefaults = {};
  if (!defaultsRoot) {
    state.collisionTypeMap = {};
    state.classParentMap = {};
    state.actuatorClassDefaults = {};
    state.actuatorClassParentMap = {};
    return;
  }

  parseCollisionDefaults(state, defaultsRoot);
  parseJointDefaults(state, defaultsRoot);
  parseMaterialDefaults(state, defaultsRoot);
  parseEqualityDefaults(state, defaultsRoot);
  parseTendonDefaults(state, defaultsRoot);
  parseActuatorDefaults(state, defaultsRoot, null, {});
};

export const parseCollisionDefaults = (
  state: CollisionDefaultsState,
  defaultElem: Element,
  parentScale: number[] | null = null,
  classMap: Record<string, number[]> = {},
  typeMap: Record<string, string> = {},
  parentClass: string | null = null,
  parentMap: Record<string, string> = {}
): void => {
  let currentScale = parentScale;
  let currentType = null;

  const meshElem = getFirstDirectChildByTag(defaultElem, "mesh");
  const geomElem = getFirstDirectChildByTag(defaultElem, "geom");
  const cameraElem = getFirstDirectChildByTag(defaultElem, "camera");
  const lightElem = getFirstDirectChildByTag(defaultElem, "light");

  if (meshElem) {
    const scaleAttr = meshElem.getAttribute("scale");
    if (scaleAttr) {
      currentScale = scaleAttr.trim().split(/\s+/).map(Number);
    }
  }
  if (geomElem) {
    const geomTypeAttr = geomElem.getAttribute("type");
    if (geomTypeAttr) currentType = geomTypeAttr;
  }

  const className = defaultElem.getAttribute("class");
  if (className && currentScale) {
    classMap[className] = currentScale;
  }
  if (className && currentType) {
    typeMap[className] = currentType;
  }
  if (className && parentClass) {
    parentMap[className] = parentClass;
  }

  // Store geom defaults from default class (material, pos, quat, size, type).
  // Root-level <default> (no class attribute) is stored under "" for fallback lookup.
  if (geomElem) {
    const effectiveClass = className || "";
    if (!state.geomClassDefaults[effectiveClass]) {
      state.geomClassDefaults[effectiveClass] = {};
    }
    const geomMaterial = geomElem.getAttribute("material");
    if (geomMaterial) {
      state.geomClassDefaults[effectiveClass].material = geomMaterial;
    }
    const geomPos = geomElem.getAttribute("pos");
    if (geomPos) {
      state.geomClassDefaults[effectiveClass].pos = geomPos.trim().split(/\s+/).map(Number);
    }
    const geomQuat = geomElem.getAttribute("quat");
    if (geomQuat) {
      state.geomClassDefaults[effectiveClass].quat = geomQuat.trim().split(/\s+/).map(Number);
    }
    const geomSize = geomElem.getAttribute("size");
    if (geomSize) {
      state.geomClassDefaults[effectiveClass].size = geomSize.trim().split(/\s+/).map(Number);
    }
    const geomFromTo = geomElem.getAttribute("fromto");
    if (geomFromTo) {
      state.geomClassDefaults[effectiveClass].fromto = geomFromTo.trim().split(/\s+/).map(Number);
    }
    const geomType = geomElem.getAttribute("type");
    if (geomType) {
      state.geomClassDefaults[effectiveClass].type = geomType;
    }
    const geomMesh = geomElem.getAttribute("mesh");
    if (geomMesh) {
      state.geomClassDefaults[effectiveClass].mesh = geomMesh;
    }
    const geomHField = geomElem.getAttribute("hfield");
    if (geomHField) {
      state.geomClassDefaults[effectiveClass].hfield = geomHField;
    }
    const geomMass = geomElem.getAttribute("mass");
    if (geomMass) {
      const parsed = parseFloat(geomMass);
      if (Number.isFinite(parsed)) {
        state.geomClassDefaults[effectiveClass].mass = parsed;
      }
    }
    const geomDensity = geomElem.getAttribute("density");
    if (geomDensity) {
      const parsed = Number(geomDensity);
      if (Number.isFinite(parsed)) {
        state.geomClassDefaults[effectiveClass].density = parsed;
      }
    }

    const geomFriction = geomElem.getAttribute("friction");
    if (geomFriction) {
      const nums = geomFriction
        .trim()
        .split(/\s+/)
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n));
      if (nums.length) {
        // Normalize to 3 values for store/export convenience (slide, roll, spin).
        // If only slide is provided (common in MJCF), keep our project defaults for the others.
        const slide = nums[0];
        const roll = nums[1] ?? 0.01;
        const spin = nums[2] ?? 0.0001;
        state.geomClassDefaults[effectiveClass].friction = [slide, roll, spin];
      }
    }

    const geomSolref = geomElem.getAttribute("solref");
    if (geomSolref) {
      const nums = geomSolref
        .trim()
        .split(/\s+/)
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n));
      if (nums.length >= 2) {
        state.geomClassDefaults[effectiveClass].solref = [nums[0], nums[1]];
      }
    }

    const geomSolimp = geomElem.getAttribute("solimp");
    if (geomSolimp) {
      const parsed = parseSolimp(geomSolimp);
      if (parsed) state.geomClassDefaults[effectiveClass].solimp = parsed;
    }

    const geomContype = geomElem.getAttribute("contype");
    if (geomContype) {
      const parsed = Number(geomContype);
      if (Number.isFinite(parsed)) {
        state.geomClassDefaults[effectiveClass].contype = parsed;
      }
    }

    const geomConaffinity = geomElem.getAttribute("conaffinity");
    if (geomConaffinity) {
      const parsed = Number(geomConaffinity);
      if (Number.isFinite(parsed)) {
        state.geomClassDefaults[effectiveClass].conaffinity = parsed;
      }
    }

    const geomCondim = geomElem.getAttribute("condim");
    if (geomCondim) {
      const parsed = Number(geomCondim);
      if (Number.isFinite(parsed)) {
        state.geomClassDefaults[effectiveClass].condim = parsed;
      }
    }

    const geomGroup = geomElem.getAttribute("group");
    if (geomGroup) {
      const parsed = Number(geomGroup);
      if (Number.isFinite(parsed)) {
        state.geomClassDefaults[effectiveClass].group = parsed;
      }
    }
  }

  if (className && cameraElem) {
    if (!state.cameraClassDefaults[className]) {
      state.cameraClassDefaults[className] = {};
    }
    const cameraPos = cameraElem.getAttribute("pos");
    if (cameraPos) {
      state.cameraClassDefaults[className].pos = cameraPos.trim().split(/\s+/).map(Number);
    }
    const cameraQuat = cameraElem.getAttribute("quat");
    if (cameraQuat) {
      state.cameraClassDefaults[className].quat = cameraQuat.trim().split(/\s+/).map(Number);
    }
    const cameraEuler = cameraElem.getAttribute("euler");
    if (cameraEuler) {
      state.cameraClassDefaults[className].euler = cameraEuler.trim().split(/\s+/).map(Number);
    }
    const cameraAxisAngle = cameraElem.getAttribute("axisangle");
    if (cameraAxisAngle) {
      state.cameraClassDefaults[className].axisangle = cameraAxisAngle
        .trim()
        .split(/\s+/)
        .map(Number);
    }
    const cameraXyaxes = cameraElem.getAttribute("xyaxes");
    if (cameraXyaxes) {
      state.cameraClassDefaults[className].xyaxes = cameraXyaxes.trim().split(/\s+/).map(Number);
    }
    const cameraZaxis = cameraElem.getAttribute("zaxis");
    if (cameraZaxis) {
      state.cameraClassDefaults[className].zaxis = cameraZaxis.trim().split(/\s+/).map(Number);
    }
  }

  if (className && lightElem) {
    if (!state.lightClassDefaults[className]) {
      state.lightClassDefaults[className] = {};
    }
    Array.from(lightElem.attributes).forEach((attr) => {
      if (attr.name && attr.value != null) {
        state.lightClassDefaults[className][attr.name] = attr.value;
      }
    });
  }

  const childDefaults = getDirectChildElementsByTag(defaultElem, "default");
  childDefaults.forEach((child: Element) =>
    parseCollisionDefaults(
      state,
      child,
      currentScale,
      classMap,
      typeMap,
      defaultElem.getAttribute("class") || parentClass,
      parentMap
    )
  );

  state.classScaleMap = classMap;
  state.collisionTypeMap = typeMap;
  state.classParentMap = parentMap;
};

export const parseJointDefaults = (
  state: JointDefaultsState,
  defaultElem: Element,
  parentClass: string | null = null
): void => {
  const className = defaultElem.getAttribute("class");
  const targetDefaults = className
    ? ((state.jointClassDefaults[className] = {}), state.jointClassDefaults[className])
    : state.jointDefaultScope;

  const jointElem = getFirstDirectChildByTag(defaultElem, "joint");
  if (jointElem) {
    const typeAttr = jointElem.getAttribute("type");
    if (typeAttr) targetDefaults.type = typeAttr;

    const axisAttr = jointElem.getAttribute("axis");
    if (axisAttr) targetDefaults.axis = axisAttr.trim().split(/\s+/).map(Number);

    const rangeAttr = jointElem.getAttribute("range");
    if (rangeAttr) targetDefaults.range = rangeAttr.trim().split(/\s+/).map(Number);

    const frictionAttr = jointElem.getAttribute("frictionloss");
    if (frictionAttr) {
      const v = parseFloat(frictionAttr);
      if (Number.isFinite(v)) targetDefaults.frictionloss = v;
    }
    const armatureAttr = jointElem.getAttribute("armature");
    if (armatureAttr) {
      const v = parseFloat(armatureAttr);
      if (Number.isFinite(v)) targetDefaults.armature = v;
    }
    const dampingAttr = jointElem.getAttribute("damping");
    if (dampingAttr) {
      const v = parseFloat(dampingAttr);
      if (Number.isFinite(v)) targetDefaults.damping = v;
    }
    const stiffnessAttr = jointElem.getAttribute("stiffness");
    if (stiffnessAttr) {
      const v = parseFloat(stiffnessAttr);
      if (Number.isFinite(v)) targetDefaults.stiffness = v;
    }
    const refAttr = jointElem.getAttribute("ref");
    if (refAttr) {
      const v = parseFloat(refAttr);
      if (Number.isFinite(v)) targetDefaults.ref = v;
    }
    const springrefAttr = jointElem.getAttribute("springref");
    if (springrefAttr) {
      const v = parseFloat(springrefAttr);
      if (Number.isFinite(v)) targetDefaults.springref = v;
    }
    const solreflimit = parseSolref(jointElem.getAttribute("solreflimit"));
    if (solreflimit) targetDefaults.solreflimit = solreflimit;

    const solimplimit = parseSolimp(jointElem.getAttribute("solimplimit"));
    if (solimplimit) targetDefaults.solimplimit = solimplimit;
  }

  if (className && parentClass) {
    state.jointClassDefaults[className].parentClass = parentClass;
  }

  const childDefaults = getDirectChildElementsByTag(defaultElem, "default");
  childDefaults.forEach((childDefault: Element) => {
    parseJointDefaults(state, childDefault, className);
  });
};

export const parseMaterialDefaults = (
  state: MaterialDefaultsState,
  defaultElem: Element,
  parentClass: string | null = null
): void => {
  const className = defaultElem.getAttribute("class");
  const targetDefaults = className
    ? ((state.materialClassDefaults[className] = {}), state.materialClassDefaults[className])
    : state.materialDefaultScope;

  const materialElem = getFirstDirectChildByTag(defaultElem, "material");
  if (materialElem) {
    if (materialElem.getAttribute("specular")) {
      targetDefaults.specular = Number(materialElem.getAttribute("specular"));
    }
    if (materialElem.getAttribute("shininess")) {
      targetDefaults.shininess = Number(materialElem.getAttribute("shininess"));
    }
    if (materialElem.getAttribute("reflectance")) {
      targetDefaults.reflectance = Number(materialElem.getAttribute("reflectance"));
    }
    if (materialElem.getAttribute("metallic")) {
      targetDefaults.metallic = Number(materialElem.getAttribute("metallic"));
    }
    if (materialElem.getAttribute("roughness")) {
      targetDefaults.roughness = Number(materialElem.getAttribute("roughness"));
    }
    if (materialElem.getAttribute("emission")) {
      targetDefaults.emission = Number(materialElem.getAttribute("emission"));
    }
  }

  if (className && parentClass) {
    state.materialClassDefaults[className].parentClass = parentClass;
  }

  const childDefaults = getDirectChildElementsByTag(defaultElem, "default");
  childDefaults.forEach((childDefault) => {
    parseMaterialDefaults(state, childDefault, className);
  });
};

export const parseEqualityDefaults = (
  state: EqualityDefaultsState,
  defaultElem: Element,
  parentClass: string | null = null
): void => {
  const className = defaultElem.getAttribute("class");
  const inherited = parentClass
    ? state.equalityClassDefaults[parentClass] || {}
    : state.equalityDefaultScope;
  const targetDefaults = className
    ? ((state.equalityClassDefaults[className] = { ...inherited }),
      state.equalityClassDefaults[className])
    : state.equalityDefaultScope;

  const equalityElem = getFirstDirectChildByTag(defaultElem, "equality");
  if (equalityElem) {
    const solref = parseSolref(equalityElem.getAttribute("solref"));
    if (solref) targetDefaults.solref = solref;

    const solimp = parseSolimp(equalityElem.getAttribute("solimp"));
    if (solimp) targetDefaults.solimp = solimp;
  }

  const childDefaults = getDirectChildElementsByTag(defaultElem, "default");
  childDefaults.forEach((childDefault: Element) => {
    parseEqualityDefaults(state, childDefault, className || parentClass);
  });
};

export const extractActuatorAttrs = (node: Element): Record<string, unknown> => {
  const getNums = (attr: string | null) =>
    attr
      ? attr
          .trim()
          .split(/\s+/)
          .map(Number)
          .filter((n) => Number.isFinite(n))
      : undefined;
  const attrs: Record<string, unknown> = {};
  const maybe = (name: string) => node.getAttribute(name);
  const ctrlrange = getNums(maybe("ctrlrange"));
  const forcerange = getNums(maybe("forcerange"));
  const gainprm = getNums(maybe("gainprm"));
  const biasprm = getNums(maybe("biasprm"));
  const dynprm = getNums(maybe("dynprm"));
  const gear = getNums(maybe("gear"));
  const gaintype = maybe("gaintype");
  const biastype = maybe("biastype");
  const dyntype = maybe("dyntype");
  const tendon = maybe("tendon");
  const site = maybe("site");
  const refsite = maybe("refsite");
  const plugin = maybe("plugin");
  const instance = maybe("instance");
  const actlimited = maybe("actlimited");
  const actrange = getNums(maybe("actrange"));
  const actdimStr = maybe("actdim");
  const kpStr = maybe("kp");
  const kvStr = maybe("kv");
  const dampratioStr = maybe("dampratio");
  const inheritrangeStr = maybe("inheritrange");
  const actearlyStr = maybe("actearly");
  if (ctrlrange && ctrlrange.length === 2) attrs.ctrlrange = [ctrlrange[0], ctrlrange[1]];
  if (forcerange && forcerange.length === 2) attrs.forcerange = [forcerange[0], forcerange[1]];
  if (gainprm) attrs.gainprm = gainprm;
  if (biasprm) attrs.biasprm = biasprm;
  if (dynprm) attrs.dynprm = dynprm;
  if (gear) attrs.gear = gear;
  if (gaintype) attrs.gaintype = gaintype;
  if (biastype) attrs.biastype = biastype;
  if (dyntype) attrs.dyntype = dyntype;
  if (tendon) attrs.tendon = tendon;
  if (site) attrs.site = site;
  if (refsite) attrs.refsite = refsite;
  if (plugin) attrs.plugin = plugin;
  if (instance) attrs.instance = instance;
  if (actlimited) attrs.actlimited = actlimited;
  if (actrange && actrange.length === 2) attrs.actrange = [actrange[0], actrange[1]];
  if (actdimStr !== null) {
    const v = Number(actdimStr);
    if (Number.isFinite(v)) attrs.actdim = v;
  }
  if (kpStr !== null) {
    const v = parseFloat(kpStr);
    if (Number.isFinite(v)) attrs.kp = v;
  }
  if (kvStr !== null) {
    const v = parseFloat(kvStr);
    if (Number.isFinite(v)) attrs.kv = v;
  }
  if (dampratioStr !== null) {
    const v = parseFloat(dampratioStr);
    if (Number.isFinite(v)) attrs.dampratio = v;
  }
  if (inheritrangeStr !== null) {
    const v = parseFloat(inheritrangeStr);
    if (Number.isFinite(v)) attrs.inheritrange = v;
  }
  if (actearlyStr !== null) {
    const v = parseFloat(actearlyStr);
    if (Number.isFinite(v)) attrs.actearly = v;
  }
  return attrs;
};

export const parseActuatorDefaults = (
  state: ActuatorDefaultsState,
  defaultElem: Element,
  parentClass: string | null = null,
  inherited: Record<string, unknown> = {}
): void => {
  const actuatorDefaultTags = [
    "general",
    "position",
    "velocity",
    "intvelocity",
    "damper",
    "motor",
    "cylinder",
    "muscle",
    "plugin",
  ];
  const actuatorDefaultElems = Array.from(defaultElem.children).filter((e: Element) =>
    actuatorDefaultTags.includes(e.tagName.toLowerCase())
  ) as Element[];
  actuatorDefaultElems.forEach((actuatorDefaultElem) => {
    const attrs = extractActuatorAttrs(actuatorDefaultElem);
    const defaultKind = actuatorDefaultElem.tagName.toLowerCase();
    const scopeClass = defaultElem.getAttribute("class");
    if (scopeClass) {
      const existing = state.actuatorClassDefaults[scopeClass] || {};
      state.actuatorClassDefaults[scopeClass] = {
        ...existing,
        ...attrs,
        _defaultKind: defaultKind,
      };
      if (parentClass) state.actuatorClassParentMap[scopeClass] = parentClass;
    } else {
      const existingGen = state.actuatorTypeDefaults[defaultKind] || {};
      state.actuatorTypeDefaults[defaultKind] = { ...existingGen, ...attrs };
      if (defaultKind !== "general") {
        const existingGenFallback = state.actuatorTypeDefaults["general"] || {};
        state.actuatorTypeDefaults["general"] = { ...existingGenFallback, ...attrs };
      }
    }
    inherited = { ...inherited, ...attrs };
  });

  const childDefaults = Array.from(defaultElem.children).filter(
    (e: Element) => e.tagName === "default"
  ) as Element[];
  childDefaults.forEach((child: Element) => {
    const childClass = child.getAttribute("class");
    if (childClass && (defaultElem.getAttribute("class") || parentClass)) {
      const p = (defaultElem.getAttribute("class") || parentClass) as string;
      state.actuatorClassParentMap[childClass] = p;
    }
    parseActuatorDefaults(
      state,
      child,
      defaultElem.getAttribute("class") || parentClass,
      inherited
    );
  });
};

export const parseTendonDefaults = (
  state: TendonDefaultsState,
  defaultElem: Element,
  parentClass: string | null = null
): void => {
  const className = defaultElem.getAttribute("class");
  const tendonElem = Array.from(defaultElem.children).find(
    (e: Element) => e.tagName.toLowerCase() === "tendon"
  ) as Element | undefined;

  if (tendonElem) {
    const attrs = parsePassthroughAttributes(tendonElem) || {};
    if (className) {
      const existing = state.tendonClassDefaults[className] || {};
      state.tendonClassDefaults[className] = { ...existing, ...attrs };
    } else {
      state.tendonDefaultScope = { ...state.tendonDefaultScope, ...attrs };
    }
  }

  const childDefaults = Array.from(defaultElem.children).filter(
    (e: Element) => e.tagName.toLowerCase() === "default"
  ) as Element[];
  childDefaults.forEach((child) => parseTendonDefaults(state, child, className || parentClass));
};
